import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import PriceMonitor from './PriceMonitor';

const PriceMonitorDashboard = () => {
  const { tokens } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const [watchedTokens, setWatchedTokens] = useState([]);
  const [newTokenMint, setNewTokenMint] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  // Default tokens to monitor
  const defaultTokens = [
    {
      mint: 'So11111111111111111111111111111111111111112',
      symbol: 'SOL',
      name: 'Solana'
    },
    {
      mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      symbol: 'USDC',
      name: 'USD Coin'
    }
  ];

  useEffect(() => {
    if (tokens?.access_token) {
      fetchWatchedTokens();
    }
  }, [tokens]);

  const fetchWatchedTokens = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/tokens/watchlist`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const watchlistTokens = data.data.watchlist.map(token => ({
          mint: token.token_address,
          symbol: token.token_symbol,
          name: token.token_name
        }));

        // Combine watchlist tokens with default tokens, with newly added tokens first
        const allTokens = [];

        // Add watchlist tokens first (newest first based on watchlist order)
        watchlistTokens.forEach(token => {
          if (!allTokens.find(t => t.mint === token.mint)) {
            allTokens.push({
              ...token,
              addedAt: new Date(), // Mark as recently added
              isFromWatchlist: true
            });
          }
        });

        // Add default tokens that aren't already in watchlist
        defaultTokens.forEach(token => {
          if (!allTokens.find(t => t.mint === token.mint)) {
            allTokens.push({
              ...token,
              addedAt: new Date(0), // Mark as default (older)
              isFromWatchlist: false
            });
          }
        });

        setWatchedTokens(allTokens);
      } else {
        // Use default tokens if watchlist fetch fails
        setWatchedTokens(defaultTokens);
      }
    } catch (error) {
      console.error('Error fetching watched tokens:', error);
      setWatchedTokens(defaultTokens);
    } finally {
      setIsLoading(false);
    }
  };

  const addTokenToMonitor = () => {
    if (!newTokenMint.trim()) {
      showError('Please enter a valid token mint address');
      return;
    }

    if (newTokenMint.length < 32 || newTokenMint.length > 44) {
      showError('Invalid Solana token address format');
      return;
    }

    if (watchedTokens.find(token => token.mint === newTokenMint)) {
      showWarning('Token is already being monitored');
      return;
    }

    const newToken = {
      mint: newTokenMint.trim(),
      symbol: 'UNKNOWN',
      name: 'Unknown Token',
      addedAt: new Date(), // Mark as newly added
      isFromWatchlist: false
    };

    // Add new token at the beginning (newest first)
    setWatchedTokens(prev => [newToken, ...prev]);
    setNewTokenMint('');
    setError(null);

    showSuccess('Token added to price monitoring!', {
      action: {
        label: 'View Token',
        onClick: () => {
          // Scroll to the newly added token
          const element = document.querySelector(`[data-token-mint="${newToken.mint}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    });
  };

  const removeTokenFromMonitor = (mintToRemove) => {
    const tokenToRemove = watchedTokens.find(token => token.mint === mintToRemove);
    setWatchedTokens(prev => prev.filter(token => token.mint !== mintToRemove));

    if (tokenToRemove) {
      showSuccess(`${tokenToRemove.symbol} removed from monitoring`);
    }
  };

  const handlePriceUpdate = (tokenMint, priceData) => {
    console.log(`Price update for ${tokenMint}:`, priceData);
    // You can add additional logic here, like triggering alerts
  };

  if (isLoading) {
    return (
      <div className="price-monitor-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading price monitoring dashboard...</p>
      </div>
    );
  }

  return (
    <div className="price-monitor-dashboard">
      <div className="dashboard-header" style={{display:'none'}}>
        <h2>Price Monitoring Dashboard</h2>
        <p>Real-time price monitoring and trend analysis for your tokens</p>
      </div>

      <div className="add-token-section">
        <div className="add-token-form">
          <input
            type="text"
            value={newTokenMint}
            onChange={(e) => setNewTokenMint(e.target.value)}
            placeholder="Enter token mint address to monitor..."
            className="token-input"
          />
          <button onClick={addTokenToMonitor} className="add-button">
            Add Token
          </button>
        </div>
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}
      </div>

      <div className="monitors-grid">
        {watchedTokens
          .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)) // Sort by newest first
          .map((token, index) => (
            <div
              key={token.mint}
              className={`monitor-card ${index === 0 && token.addedAt > new Date(Date.now() - 5000) ? 'newly-added' : ''}`}
              data-token-mint={token.mint}
            >
              <div className="monitor-header">
                <div className="token-info">
                  <div className="token-title">
                    <h3 style={{color: 'lightgray'}}>{token.symbol}</h3>
                    {token.isFromWatchlist && (
                      <span className="watchlist-badge" title="From your watchlist">⭐</span>
                    )}
                    {index === 0 && token.addedAt > new Date(Date.now() - 5000) && (
                      <span className="new-badge" title="Recently added">NEW</span>
                    )}
                  </div>
                  <p style={{ color: 'white' }}>{token.name}</p>
                  <span className="token-address">
                    {token.mint.slice(0, 6)}...{token.mint.slice(-4)}
                  </span>
                </div>
                <button
                  onClick={() => removeTokenFromMonitor(token.mint)}
                  className="remove-button"
                  title="Remove from monitoring"
                >
                  ×
                </button>
              </div>

              <PriceMonitor
                tokenMint={token.mint}
                onPriceUpdate={(priceData) => handlePriceUpdate(token.mint, priceData)}
                showTrend={true}
                showHistory={false}
              />
            </div>
          ))}
      </div>

      {watchedTokens.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Tokens Being Monitored</h3>
          <p>Add token mint addresses above to start monitoring their prices in real-time</p>
        </div>
      )}

      <div className="dashboard-info">
        <div className="info-card" >
          <h4>🔄 Real-time Updates</h4>
          <p>Prices update every 30 seconds via WebSocket connection</p>
        </div>
        <div className="info-card" >
          <h4 >📈 Trend Analysis</h4>
          <p style={{color:'grey'}}>Advanced algorithms analyze price movements and detect trends</p>
        </div>
        <div className="info-card">
          <h4>📊 Historical Data</h4>
          <p>Price history is stored for trend analysis and performance tracking</p>
        </div>
      </div>
    </div>
  );
};

export default PriceMonitorDashboard;